import React, { type FunctionComponent } from 'react';
import classNames from 'classnames';
import { Box, Stack, Typography } from '@mui/material';

import BarGraphIcon from 'components/Icons/BarGraphIcon';
import WindStationIcon from 'components/Icons/WindStationIcon';

import styles from './GraphLegend.module.scss';

interface LegendItemProps {
  icon: React.ReactNode;
  label: string;
  textClassName: string;
  dataTestId?: string;
  isActive?: boolean;
}

const LegendItem: FunctionComponent<LegendItemProps> = ({
  icon,
  label,
  textClassName,
  dataTestId,
  isActive = true,
}) => (
  <Stack
    alignItems="center"
    className={classNames({
      [styles.legendItem]: true,
      [styles.inactive]: !isActive,
    })}
    data-testid={dataTestId}
    direction="row"
    spacing={0.5}
  >
    <Box className={styles.icon}>{icon}</Box>
    <Typography variant="caption1" className={classNames(textClassName)}>
      {label}
    </Typography>
  </Stack>
);

interface GraphLegendProps {
  dataTestId?: string;
  hasLiveWind?: boolean;
  isCurrentWindLive?: boolean;
}

const GraphLegend: FunctionComponent<GraphLegendProps> = ({
  dataTestId = 'forecast-graph-wind-legend',
  hasLiveWind = false,
  isCurrentWindLive = false,
}) => {
  const forecastIconClassName = classNames({
    [styles.forecastIcon]: true,
    [styles.inactive]: isCurrentWindLive,
  });

  const forecastTextClassName = classNames({
    [styles.forecastText]: true,
    [styles.inactive]: isCurrentWindLive,
  });

  const observationIconClassName = classNames({
    [styles.observationIcon]: true,
    [styles.inactive]: !isCurrentWindLive,
  });

  const observationTextClassName = classNames({
    [styles.observationText]: true,
    [styles.inactive]: !isCurrentWindLive,
  });

  return (
    <Stack direction="row" spacing={2} width="100%" data-testid={dataTestId}>
      <LegendItem
        icon={
          <BarGraphIcon className={forecastIconClassName} clipPathId="legend-bar-graph-icon-wind" />
        }
        textClassName={forecastTextClassName}
        label="GFS Forecast"
        dataTestId={`${dataTestId}-gfs-forecast`}
        isActive={!isCurrentWindLive}
      />
      {hasLiveWind && (
        <LegendItem
          icon={<WindStationIcon className={observationIconClassName} />}
          label="Wind Station"
          textClassName={observationTextClassName}
          dataTestId={`${dataTestId}-wind-station`}
          isActive={isCurrentWindLive}
        />
      )}
    </Stack>
  );
};

export default GraphLegend;
